import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  ArrowUpRight, 
  Smartphone, 
  Zap, 
  ShieldCheck, 
  Globe, 
  Lock 
} from "lucide-react";

export const LandingScreen = () => {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f3f0] text-[#111317] font-body selection:bg-black selection:text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="relative z-20 px-6 py-6 max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-[#111317] rounded-xl flex items-center justify-center font-display font-black text-lg text-white">
            S
          </div>
          <span className="font-display font-bold text-xl tracking-tight">Shwiip</span>
        </div>
        
        <div className="hidden md:flex items-center gap-8 font-medium text-gray-500">
          <a href="#product" className="hover:text-black transition-colors">Product</a>
          <a href="#compliance" className="hover:text-black transition-colors">Compliance</a>
          <a href="#partners" className="hover:text-black transition-colors">Partners</a>
          <a href="#faq" className="hover:text-black transition-colors">FAQ</a>
        </div>

        <button 
          onClick={() => nav("/demo")}
          className="px-5 py-2.5 bg-[#111317] hover:bg-black text-white rounded-full font-bold text-[14px] transition-all flex items-center gap-2"
        >
          Open app
          <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="max-w-2xl text-center md:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm text-[11px] font-bold text-gray-500 mb-8 uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Technical Service Provider · Not a bank
              </div>
              
              <h1 className="font-display text-[54px] md:text-[84px] font-bold tracking-tight mb-8 leading-[0.9] text-[#111317]">
                Send money,<br/>
                <span className="text-gray-400">transparently.</span>
              </h1>

              <p className="text-gray-500 text-lg md:text-xl mb-12 leading-relaxed font-medium">
                Shwiip is the cleanest way to move money to a phone number. We orchestrate the rails — every transfer is initiated, processed and settled by a licensed partner. You always know who's moving your money.
              </p>

              <div className="flex flex-wrap gap-4 items-center justify-center md:justify-start">
                <button
                  onClick={() => nav("/demo")}
                  className="px-10 py-5 bg-[#111317] hover:bg-black text-white rounded-[24px] font-bold text-lg transition-all flex items-center gap-2 shadow-xl shadow-black/10 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Try the app
                  <ArrowUpRight className="w-5 h-5" strokeWidth={3} />
                </button>
              </div>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex-1 w-full max-w-[600px] flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/10 blur-[100px] rounded-full" />
              <img 
                src="/landingpage-removebg-preview.png" 
                alt="Shwiip Demo" 
                className="relative z-10 w-full h-auto max-h-[480px] object-contain drop-shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Section */}
      <section id="product" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">Product</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#111317]">Built for trust at every tap.</h2>
          <p className="text-gray-500 mt-4 max-w-xl mx-auto font-medium">
            A focused payment experience that hides complexity without hiding accountability.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
             <FeatureCard 
               icon={<Smartphone className="w-6 h-6 text-white" />}
               title="Mobile-first by design"
               desc="Touch-tuned amount keypad, biometric auth, resumable state if the app is closed mid-transfer."
             />
          </div>
          <div className="md:col-span-1">
            <FeatureCard 
               icon={<Zap className="w-6 h-6 text-white" />}
               title="Smart routing"
               desc="Our orchestration engine picks the fastest licensed rail — bank, wallet, or fallback."
             />
          </div>
          <div className="md:col-span-1">
            <FeatureCard 
               icon={<ShieldCheck className="w-6 h-6 text-white" />}
               title="Compliance UX"
               desc="Mandatory partner attribution on confirm, processing, success and history rows."
             />
          </div>
          <div className="md:col-span-2">
            <FeatureCard 
               icon={<Globe className="w-6 h-6 text-white" />}
               title="Cross-border ready"
               desc="Send to any phone number. Recipient picks how to cash out — or keeps it in their wallet."
             />
          </div>
          <div className="md:col-span-1">
            <FeatureCard 
               icon={<Lock className="w-6 h-6 text-white" />}
               title="Zero custody"
               desc="Shwiip never holds your money. Funds move from your bank to the recipient via the partner."
             />
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="relative overflow-hidden rounded-[40px] bg-[#0c0e12] p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10">
          <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-blue-600/20 blur-[80px] rounded-full translate-x-1/2 -translate-y-1/2 pointer-events-none" />
          
          <div className="relative z-10 max-w-lg text-center md:text-left">
            <h3 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Try Shwiip in your browser.
            </h3>
            <p className="text-gray-400 text-lg font-medium">
              No download. The full mobile flow runs as a web app — perfect on phone, desktop or tablet.
            </p>
          </div>

          <button 
            onClick={() => nav("/demo")}
            className="relative z-10 px-8 py-4 bg-white hover:bg-gray-100 text-[#111317] rounded-[20px] font-bold text-lg transition-all flex items-center gap-3 whitespace-nowrap shadow-xl"
          >
            Open the app
            <ArrowUpRight className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-6 py-16 border-t border-gray-200 mt-20">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
          <div>
            <div className="flex items-center gap-2 justify-center md:justify-start mb-4">
              <div className="w-8 h-8 bg-[#111317] rounded-lg flex items-center justify-center font-display font-black text-lg text-white">
                S
              </div>
              <span className="font-display font-bold text-lg tracking-tight">Shwiip</span>
            </div>
            <p className="text-gray-400 text-sm font-medium">
              Shwiip acts as a technical interface only. Funds are moved by licensed partners.
            </p>
          </div>
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest leading-loose">
            © 2026 Shwiip Technical Service Provider.<br/>
            Regulated partners: Finshark, Fasto, Zimpler.
          </p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, desc }) => (
  <div className="p-10 rounded-[32px] bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow h-full">
    <div className="w-12 h-12 rounded-2xl bg-[#111317] flex items-center justify-center mb-10">
      {icon}
    </div>
    <h4 className="font-display font-bold text-[22px] mb-4 text-[#111317] tracking-tight">{title}</h4>
    <p className="text-gray-400 text-sm leading-relaxed font-medium">{desc}</p>
  </div>
);

export default LandingScreen;
